import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { isTokenExpired } from '@/lib/jwt';
import type { AuthUser, TenantWithRole } from '@/types/auth';
import type { Role } from '@/types/domain';

/**
 * Estado global de sesión (Zustand + persistencia en localStorage).
 *
 * Guarda SOLO lo que el shell necesita en todas las pantallas: el JWT, el usuario, sus
 * tenants y cuál está activo. Los datos de cada módulo (productos, órdenes…) viven en la
 * caché de TanStack Query, no aquí.
 *
 * El token se persiste para sobrevivir un refresh de la página. Al recibir un 401,
 * `src/lib/session-guard.ts` llama a `clearSession()` y vacía la caché de queries.
 */

export interface AuthSession {
  token: string;
  user: AuthUser;
  tenants: TenantWithRole[];
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  /** Tenants (negocios) a los que pertenece el usuario, con su rol en cada uno. */
  tenants: TenantWithRole[];
  /** Tenant activo: el que usan todas las queries (prefijo de las query keys). */
  activeTenantId: string | null;

  setSession: (session: AuthSession) => void;
  setUser: (user: AuthUser) => void;
  setTenants: (tenants: TenantWithRole[]) => void;
  setActiveTenant: (tenantId: string) => void;
  clearSession: () => void;
}

/**
 * Mantiene el tenant activo si sigue existiendo; si no, cae al primero.
 * (Un usuario puede pertenecer a varios negocios y cambiar entre ellos sin re-loguearse.)
 */
function pickActiveTenantId(current: string | null, tenants: TenantWithRole[]): string | null {
  if (current && tenants.some((tenant) => tenant.id === current)) return current;
  return tenants[0]?.id ?? null;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      tenants: [],
      activeTenantId: null,

      setSession: ({ token, user, tenants }) =>
        set((state) => ({
          token,
          user,
          tenants,
          activeTenantId: pickActiveTenantId(state.activeTenantId, tenants),
        })),

      setUser: (user) => set({ user }),

      setTenants: (tenants) =>
        set((state) => ({
          tenants,
          activeTenantId: pickActiveTenantId(state.activeTenantId, tenants),
        })),

      setActiveTenant: (tenantId) => set({ activeTenantId: tenantId }),

      clearSession: () => set({ token: null, user: null, tenants: [], activeTenantId: null }),
    }),
    {
      name: 'wa-sales.auth',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        tenants: state.tenants,
        activeTenantId: state.activeTenantId,
      }),
    },
  ),
);

/* ---------------------------------------------------------------------------
 * Selectores (úsalos siempre así: `useAuthStore(selectToken)`).
 * ------------------------------------------------------------------------- */

export const selectToken = (state: AuthState): string | null => state.token;

export const selectUser = (state: AuthState): AuthUser | null => state.user;

export const selectTenants = (state: AuthState): TenantWithRole[] => state.tenants;

/** Tenant activo (o el primero como respaldo). `null` si el usuario no tiene ninguno. */
export const selectActiveTenant = (state: AuthState): TenantWithRole | null =>
  state.tenants.find((tenant) => tenant.id === state.activeTenantId) ?? state.tenants[0] ?? null;

/** Rol del usuario en el tenant activo (para ocultar acciones solo-ADMIN). */
export const selectActiveRole = (state: AuthState): Role | null => selectActiveTenant(state)?.role ?? null;

/**
 * ¿Hay sesión utilizable? Comprueba que exista el token Y que no haya expirado
 * (el `exp` del JWT se lee en `src/lib/jwt.ts`; la firma la valida el backend).
 */
export const selectIsAuthenticated = (state: AuthState): boolean =>
  Boolean(state.token) && !isTokenExpired(state.token);

/* ---------------------------------------------------------------------------
 * Acceso fuera de React (cliente HTTP, guardas de navegación).
 * ------------------------------------------------------------------------- */

/** Token de la sesión activa. Lo usa el cliente HTTP para el header `Authorization`. */
export function getStoredToken(): string | null {
  return useAuthStore.getState().token;
}

/** ¿La sesión sigue vigente? (sin montar React). */
export function hasValidSession(): boolean {
  return selectIsAuthenticated(useAuthStore.getState());
}
